import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { ArrowRight, GitBranch, History, Users } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { StageBadge } from '@/components/shared/stage-badge'
import { useLifecycleStages } from '@/hooks/use-lifecycle-stages'
import { DataTable, type Column } from '@/components/shared/data-table'
import { EmptyState, Loading } from '@/components/shared/feedback'
import { formatNumber } from '@/lib/utils'
import { useLifecyclePipeline, useRecentLifecycleChanges, type CdpEvent } from '@/hooks/use-cdp'
import { QueryError, formatDateTime } from './cdp-shared'

interface LifecycleChangeProps {
  fromStage?: string | null
  toStage?: string | null
  reason?: string | null
}

/** Phễu khách theo giai đoạn + các lần chuyển giai đoạn gần đây — phần dưới của Cài đặt → Pipeline khách hàng. */
export function LifecycleTab() {
  const { t } = useTranslation('cdp')
  const { keys: stageKeys, stages } = useLifecycleStages()
  const pipelineQuery = useLifecyclePipeline()
  const changesQuery = useRecentLifecycleChanges(30)

  const { counts, total, unknown } = useMemo(() => {
    const map: Record<string, number> = {}
    let sum = 0
    let other = 0
    for (const row of pipelineQuery.data ?? []) {
      const stage = row.lifecycleStage ?? row.status
      const n = row._count?._all ?? 0
      sum += n
      if (stageKeys.includes(stage)) map[stage] = (map[stage] ?? 0) + n
      else other += n
    }
    return { counts: map, total: sum, unknown: other }
  }, [pipelineQuery.data, stageKeys])

  const maxCount = Math.max(1, ...stageKeys.map((s) => counts[s] ?? 0))

  const columns: Column<CdpEvent>[] = [
    {
      key: 'time',
      header: t('lifecycle.colTime'),
      cell: (e) => <span className="whitespace-nowrap text-muted-foreground">{formatDateTime(e.timestamp)}</span>,
    },
    {
      key: 'contact',
      header: t('lifecycle.colCustomer'),
      cell: (e) =>
        e.contact ? (
          <Link to={`/customers/${e.contact.id}`} className="font-medium hover:underline">
            {e.contact.fullName || t('lifecycle.noName')}
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'change',
      header: t('lifecycle.colChange'),
      cell: (e) => {
        const p = (e.properties ?? {}) as LifecycleChangeProps
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            {p.fromStage ? (
              <StageBadge stage={p.fromStage} />
            ) : (
              <span className="text-xs text-muted-foreground">{t('lifecycle.newStage')}</span>
            )}
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
            <StageBadge stage={p.toStage} />
          </div>
        )
      },
    },
    {
      key: 'reason',
      header: t('lifecycle.colReason'),
      cell: (e) => {
        const p = (e.properties ?? {}) as LifecycleChangeProps
        return <span className="text-muted-foreground">{p.reason || '—'}</span>
      },
    },
  ]

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">{t('lifecycle.funnelTitle')}</h2>
          <p className="text-xs text-muted-foreground">{t('lifecycle.funnelSubtitle')}</p>
        </div>
        {pipelineQuery.isLoading ? (
          <Loading label={t('lifecycle.funnelLoading')} />
        ) : pipelineQuery.isError ? (
          <QueryError error={pipelineQuery.error} what={t('lifecycle.funnelWhat')} />
        ) : (
          <Card>
            <CardContent className="space-y-4 pt-5">
              <div className="flex flex-wrap items-baseline gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-2xl font-bold tabular-nums">{formatNumber(total)}</span>
                <span className="text-sm text-muted-foreground">{t('lifecycle.customers')}</span>
                {unknown > 0 && (
                  <span className="text-xs text-muted-foreground">
                    · {t('lifecycle.unknownStage', { n: formatNumber(unknown) })}
                  </span>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4">
                {stages.map((s, i) => {
                  const n = counts[s.key] ?? 0
                  const pct = total ? Math.round((n / total) * 100) : 0
                  return (
                    <div key={s.key} className="rounded-lg border bg-background p-3">
                      <div className="flex items-center justify-between gap-2">
                        <StageBadge stage={s.key} />
                        <span className="text-[10px] text-muted-foreground">#{i + 1}</span>
                      </div>
                      <p className="mt-2 text-2xl font-bold tabular-nums">{formatNumber(n)}</p>
                      <p className="text-xs text-muted-foreground">{pct}%</p>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{
                            width: `${Math.max(n ? 3 : 0, (n / maxCount) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">{t('lifecycle.recentTitle')}</h2>
          <p className="text-xs text-muted-foreground">
            <Trans t={t} i18nKey="lifecycle.recentSubtitle" values={{ count: 30 }} components={{ code: <code /> }} />
          </p>
        </div>
        {changesQuery.isError ? (
          <QueryError error={changesQuery.error} what={t('lifecycle.historyWhat')} />
        ) : changesQuery.isLoading ? (
          <Loading label={t('lifecycle.historyLoading')} />
        ) : !changesQuery.data?.events.length ? (
          <EmptyState
            icon={History}
            title={t('lifecycle.emptyTitle')}
            description={t('lifecycle.emptyDescription')}
          />
        ) : (
          <DataTable columns={columns} rows={changesQuery.data.events} rowKey={(e) => e.id} />
        )}
      </section>

      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <GitBranch className="h-3.5 w-3.5" /> {t('lifecycle.changeHint')}
      </p>
    </div>
  )
}
